<script setup>
import { computed, onActivated, onDeactivated, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import {
  useStore,
  useStoreGetters,
  useMapGetter,
} from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { frontendURL } from 'dashboard/helper/URLHelper';
import CampaignsAPI from 'dashboard/api/campaigns';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import VoiceCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/VoiceCampaign/VoiceCampaignDialog.vue';
import RenameCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/RenameCampaignDialog.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();
const route = useRoute();
const router = useRouter();

const selectedCampaign = ref(null);
const voiceCampaignDialogRef = ref(null);
const renameCampaignDialogRef = ref(null);
const confirmDeleteCampaignDialogRef = ref(null);

const { isAdmin } = useAdmin();

const uiFlags = useMapGetter('campaigns/getUIFlags');
// An agent is not allowed to list the account's campaigns, so theirs do not come from the store:
// the server answers which ones they may enter, and that is the whole list they get.
const dialableCampaigns = ref([]);
const isFetchingDialable = ref(false);

const isFetchingCampaigns = computed(() =>
  isAdmin.value ? uiFlags.value.isFetching : isFetchingDialable.value
);

const voiceCampaigns = computed(() =>
  isAdmin.value
    ? getters['campaigns/getVoiceCampaigns'].value
    : dialableCampaigns.value
);

const fetchDialable = async () => {
  isFetchingDialable.value = true;
  try {
    const { data } = await CampaignsAPI.dialable();
    dialableCampaigns.value = data;
  } finally {
    isFetchingDialable.value = false;
  }
};

onMounted(() => {
  if (!isAdmin.value) fetchDialable();
});

// The list stays cached while a campaign's page is open, and a campaign turned on or off over the API
// in the meantime came back with its old status on the card.
let hasBeenAway = false;
onDeactivated(() => {
  hasBeenAway = true;
});
onActivated(() => {
  if (!hasBeenAway) return;
  if (isAdmin.value) store.dispatch('campaigns/get');
  else fetchDialable();
});

const hasNoVoiceCampaigns = computed(
  () => voiceCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const openCreateCampaignDialog = () => {
  voiceCampaignDialogRef.value?.dialogRef.open();
};

// Creating a campaign lands on its panel: that is where an agent joins it and where the operation
// is watched, so the flow does not stop at a card in a list.
const openCampaign = campaign => {
  if (!campaign?.id) return;
  router.push(
    frontendURL(
      `accounts/${route.params.accountId}/campaigns/voice/${campaign.id}`
    )
  );
};

const handleRename = campaign => renameCampaignDialogRef.value.open(campaign);

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.VOICE.HEADER_TITLE')"
    :button-label="isAdmin ? t('CAMPAIGN.VOICE.NEW_CAMPAIGN') : ''"
    @click="openCreateCampaignDialog"
  >
    <div
      v-if="isFetchingCampaigns && !voiceCampaigns.length"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <CampaignList
      v-else-if="!hasNoVoiceCampaigns"
      :campaigns="voiceCampaigns"
      is-voice-type
      :read-only="!isAdmin"
      @rename="handleRename"
      @delete="handleDelete"
      @open="openCampaign"
    />
    <div
      v-else
      class="flex flex-col items-center gap-2 pt-14 text-center text-n-slate-11"
    >
      <span class="text-base font-medium text-n-slate-12">
        {{ t('CAMPAIGN.VOICE.EMPTY_STATE.TITLE') }}
      </span>
      <p class="max-w-md text-sm">
        {{ t('CAMPAIGN.VOICE.EMPTY_STATE.SUBTITLE') }}
      </p>
    </div>
    <VoiceCampaignDialog ref="voiceCampaignDialogRef" @created="openCampaign" />
    <RenameCampaignDialog ref="renameCampaignDialogRef" />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
  </CampaignLayout>
</template>
